import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import LatestPost from "../components/LatestPost";

import IconCalendar from "../assets/img/calendar.svg";
import IconUser from "../assets/img/user.svg";
import bannerImg from "../assets/img/blog_banner.jpg"; // FIXED banner image

// ====== CONFIG ======
const SITE = "https://workdayz.in/formdata/";   
const API_SHOW = `${SITE}/api_blog_show.php`;

const prettyDate = (iso) => {
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch {
    return iso || "";
  }
};

const absolutize = (maybeRelative) => {
  if (!maybeRelative) return "";
  try { return new URL(maybeRelative, SITE).toString(); } catch { return maybeRelative; }
};

export default function Post() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => { window.scrollTo(0, 0); }, [slug]);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setLoading(true);
      setErr("");
      try {
        const url = new URL(API_SHOW);
        url.searchParams.set("slug", slug);
        const res = await fetch(url, { signal: controller.signal });
        const json = await res.json();
        if (!json?.success || !json?.data) throw new Error("Not found");

        const row = json.data;
        const cover = row.cover_path ? absolutize(row.cover_path) : "";
        const metaTitle = row.meta_title || row.title || "Blog";
        const metaDesc  = row.meta_description || row.excerpt || "";
        const metaKeys  = row.meta_keywords || "";
        const author    = row.author || "Admin";
        const dateStr   = prettyDate(row.created_at);

        setPost({
          id: row.id,
          slug: row.slug,
          title: row.title,
          cover,
          author,
          date: dateStr,
          content: row.content || "",
          metaTitle, metaDesc, metaKeys,
        });
      } catch (e) {
        if (e.name !== "AbortError") setErr("Post not found.");
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [slug]);

  if (loading) return <div style={{ padding: 16 }}>Loading…</div>;
  if (err) return <div style={{ padding: 16, color: "tomato" }}>{err}</div>;
  if (!post) return <div style={{ padding: 16 }}>Not found.</div>;

  const canonical = `${SITE}/blog/${post.slug}`;

  return (
    <>
      {/* SEO */}
      <Helmet>
        <title>{post.metaTitle}</title>
        {post.metaDesc && <meta name="description" content={post.metaDesc} />}
        {post.metaKeys && <meta name="keywords" content={post.metaKeys} />}
        <link rel="canonical" href={canonical} />

        {/* OG/Twitter */}
        <meta property="og:title" content={post.metaTitle} />
        {post.metaDesc && <meta property="og:description" content={post.metaDesc} />}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonical} />
        {post.cover && <meta property="og:image" content={post.cover} />}
      </Helmet>

      {/* FIXED banner */}
      <section className="banner">
        <div
          className="banner_image blog_image"
          style={{
            backgroundImage: `url(${bannerImg})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
          }}
        >
          <div className="container">
            <div className="banner_text">
              <h2 className="blog_title">Blog</h2>
              <p>{post.title}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Content + Sidebar */}
      <section className="blog_latest_post">
        <div className="blog_latest_details">
          <div className="container">
            <div className="row">
              {/* Left */}
              <div className="col-sm-8">

                {/* 1. Cover Image */}
                {post.cover && (
                  <div className="repair" style={{ marginBottom: 20 }}>
                    <img
                      src={post.cover}
                      alt={post.title}
                      style={{ width: "100%", borderRadius: 12 }}
                    />
                  </div>
                )}

                {/* 2. Title */}
                <h2 style={{ marginBottom: 12 }}>{post.title}</h2>

                {/* 3. Meta Row */}
                <div
                  className="admin_date admin_border"
                  style={{ display: "flex", gap: 18, alignItems: "center", margin: "16px 0" }}
                >
                  <div className="admin date" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src={IconCalendar} alt="date" width={22} height={22} />
                    <p style={{ margin: 0 }}>{post.date}</p>
                  </div>
                  <div className="admin date" style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <img src={IconUser} alt="author" width={22} height={22} />
                    <p style={{ margin: 0 }}>{post.author}</p>
                  </div>
                </div>

                {/* 4. Content */}
                <div
                  className="blog_contents"
                  dangerouslySetInnerHTML={{ __html: post.content }}
                />
              </div>

              {/* Right */}
              <div className="col-sm-4">
                <LatestPost />
                <div style={{ marginTop: 24 }}>
                  <Link to="/blog" className="btn btn--ghost">← Back to Blog</Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
